import React from 'react';
import ValidationRuleBuilder from './ValidationRuleBuilder';
import ConditionalLogicBuilder from './ConditionalLogicBuilder';

const FIELD_TYPES = [
  'text',
  'email',
  'number',
  'password',
  'textarea',
  'select',
  'multiselect',
  'radio',
  'checkbox',
  'date',
  'file',
];

function parseOptions(value) {
  if (!value) return null;
  return value.split(',').map((v) => v.trim()).filter(Boolean);
}

function stringifyOptions(value) {
  if (!value) return '';
  return Array.isArray(value) ? value.join(', ') : '';
}

export default function FieldEditor({ field, onChange, fieldOptions }) {
  if (!field) {
    return <div className="text-sm text-gray-500 dark:text-slate-400">Select a field to edit.</div>;
  }

  const showOptions = ['select', 'multiselect', 'radio'].includes(field.fieldType);

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700 dark:text-slate-300">Field Settings</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Label</label>
          <input
            type="text"
            value={field.label || ''}
            onChange={(e) => onChange({ ...field, label: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Field Key</label>
          <input
            type="text"
            value={field.fieldKey || ''}
            onChange={(e) => onChange({ ...field, fieldKey: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Field Type</label>
          <select
            value={field.fieldType}
            onChange={(e) => onChange({ ...field, fieldType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white"
          >
            {FIELD_TYPES.map((t) => (
              <option key={t} value={t} className="bg-slate-800 text-white">
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Placeholder</label>
          <input
            type="text"
            value={field.placeholder || ''}
            onChange={(e) => onChange({ ...field, placeholder: e.target.value || null })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Help Text</label>
          <input
            type="text"
            value={field.helpText || ''}
            onChange={(e) => onChange({ ...field, helpText: e.target.value || null })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Group</label>
          <select
            value={field.group || ''}
            onChange={(e) => onChange({ ...field, group: e.target.value || null })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white"
          >
            <option value="" className="bg-slate-800 text-white">Default</option>
            <option value="quick" className="bg-slate-800 text-white">Quick (chips)</option>
            <option value="standard" className="bg-slate-800 text-white">Standard</option>
            <option value="advanced" className="bg-slate-800 text-white">Advanced</option>
            <option value="hidden" className="bg-slate-800 text-white">Hidden</option>
          </select>
        </div>
      </div>

      {showOptions && (
        <div>
          <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Options (comma separated)</label>
          <input
            type="text"
            value={stringifyOptions(field.options)}
            onChange={(e) => onChange({ ...field, options: parseOptions(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white"
          />
        </div>
      )}

      <div>
        <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Default Value</label>
        <input
          type="text"
          value={field.defaultValue ?? ''}
          onChange={(e) => onChange({ ...field, defaultValue: e.target.value === '' ? null : e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="flex items-center gap-2 text-gray-700 dark:text-slate-200 text-sm font-medium">
          <input
            type="checkbox"
            checked={!!field.isRequired}
            onChange={(e) => onChange({ ...field, isRequired: e.target.checked })}
            className="w-4 h-4"
          />
          Required
        </label>
        <label className="flex items-center gap-2 text-gray-700 dark:text-slate-200 text-sm font-medium">
          <input
            type="checkbox"
            checked={field.isActive !== false}
            onChange={(e) => onChange({ ...field, isActive: e.target.checked })}
            className="w-4 h-4"
          />
          Active
        </label>
      </div>

      <ValidationRuleBuilder
        value={field.validationRules}
        onChange={(rules) => onChange({ ...field, validationRules: rules })}
      />

      <ConditionalLogicBuilder
        value={field.conditionalLogic}
        onChange={(logic) => onChange({ ...field, conditionalLogic: logic })}
        fieldOptions={fieldOptions}
      />
    </div>
  );
}


